// Copyright © 2026 Atomic.io Limited. All rights reserved.
import {useEffect, useRef, useState} from 'react'
import {setTokenFetchFailure} from '../api/token'
import {Settings} from '../util/settings'
import {parseFlushIntervalInput} from '../util/flush-interval'
import {subscribeToSDKEvents} from '../util/sdk-event-bus'

// Storage keys the SDK keeps its analytics queues under. The authenticated queue key is prefixed with the user ID.
const UNAUTHENTICATED_QUEUE_KEY = 'AtomicAnalyticsQueue'
const AUTHENTICATED_QUEUE_SUFFIX = '_AuthenticatedAtomicAnalyticsQueue'

const COLLAPSED_STORAGE_KEY = 'ANALYTICS_PANEL_COLLAPSED'
const SHOW_SDK_EVENTS_STORAGE_KEY = 'ANALYTICS_PANEL_SHOW_SDK_EVENTS'
const QUEUE_POLL_INTERVAL_MS = 1000
const MAX_LOG_ENTRIES = 200
const CONCURRENT_FLUSH_COUNT = 3
const SDK_LOG_PREFIX = 'AtomicSDK Web -'

// Longer than the lifetime of any token, so the SDK treats its cached token as expired and calls the session delegate.
const FORCE_TOKEN_REQUEST_EXPIRY_INTERVAL = 10 * 365 * 24 * 60 * 60
const DEFAULT_TOKEN_EXPIRY_INTERVAL = 60

const INTERVAL_PRESETS = [
  '5',
  '15',
  '30',
  '86400',
  '4',
  '86401',
  'Infinity',
  'NaN',
  "'15'",
  'null',
]

// Reads a remembered panel preference, `true` unless it was stored as `false`.
const readStoredFlag = key => {
  try {
    return localStorage.getItem(key) !== 'false'
  } catch {
    return true
  }
}

const storeFlag = (key, value) => {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Storage unavailable, the panel preference is not remembered.
  }
}

const readQueue = key => {
  const raw = localStorage.getItem(key)
  if (raw === null) {
    return {events: [], malformed: false}
  }

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return {events: parsed, malformed: false}
    }
  } catch {
    // Reported as malformed below.
  }

  return {events: [], malformed: true}
}

/**
 * Reads the SDK's analytics queues from local storage, the unauthenticated queue first, then one per user.
 */
const readQueues = () => {
  try {
    const queues = [
      {
        key: UNAUTHENTICATED_QUEUE_KEY,
        label: 'Unauthenticated',
        ...readQueue(UNAUTHENTICATED_QUEUE_KEY),
      },
    ]

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index)
      if (key && key.endsWith(AUTHENTICATED_QUEUE_SUFFIX)) {
        const userId = key.slice(0, -AUTHENTICATED_QUEUE_SUFFIX.length)
        queues.push({key, label: `User ${userId}`, ...readQueue(key)})
      }
    }

    return queues
  } catch {
    return []
  }
}

const queueSignature = queues =>
  queues
    .map(
      queue =>
        `${queue.key}:${queue.malformed}:${queue.events
          .map(event => event.id)
          .join(',')}`
    )
    .join('|')

const totalQueued = queues =>
  queues.reduce((total, queue) => total + queue.events.length, 0)

const eventName = event => event.eventName || event.analyticsEvent || 'unknown'

const formatTime = date => {
  const time = date.toLocaleTimeString([], {hour12: false})
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0')
  return `${time}.${milliseconds}`
}

const describeError = error => {
  if (!error) {
    return 'Unknown error'
  }

  const parts = [`${error.name}: ${error.message}`]
  if (error.cause) {
    parts.push(`cause → ${error.cause.name}: ${error.cause.message}`)
  }
  return parts.join('\n')
}

const formatLogArgument = argument => {
  if (typeof argument === 'string') {
    return argument
  }
  if (argument instanceof Error) {
    return `${argument.name}: ${argument.message}`
  }
  try {
    return JSON.stringify(argument)
  } catch {
    return String(argument)
  }
}

/**
 * Runs `callback` and returns the SDK warnings it logged to the console. Warnings are still logged as usual.
 */
const captureSDKWarnings = callback => {
  const warnings = []
  const originalWarn = console.warn

  console.warn = (...args) => {
    if (args[0] === SDK_LOG_PREFIX) {
      warnings.push(args.slice(1).map(formatLogArgument).join(' '))
    }
    originalWarn.apply(console, args)
  }

  try {
    callback()
  } finally {
    console.warn = originalWarn
  }

  return warnings
}

const elapsedSince = started => Math.round(performance.now() - started)

/**
 * Controls for testing analytics event delivery: `AtomicSDK.flushEvents`, `AtomicSDK.setEventFlushInterval` and
 * `AtomicSDK.setApiProtocol`, with a live view of the SDK's queued events and a log of every result.
 */
export const AnalyticsEventsPanel = () => {
  const [collapsed, setCollapsed] = useState(() =>
    readStoredFlag(COLLAPSED_STORAGE_KEY)
  )
  const [showSDKEvents, setShowSDKEvents] = useState(() =>
    readStoredFlag(SHOW_SDK_EVENTS_STORAGE_KEY)
  )
  const [sdkEventFilter, setSDKEventFilter] = useState('')
  const [queues, setQueues] = useState(readQueues)
  const [log, setLog] = useState([])
  const [intervalInput, setIntervalInput] = useState('15')
  const [customEventName, setCustomEventName] = useState('qa-flush-test')
  const [tokenFetchFails, setTokenFetchFails] = useState(false)
  const [flushesInProgress, setFlushesInProgress] = useState(0)

  const nextLogId = useRef(0)
  const flushesInProgressRef = useRef(0)
  const lastFlushSettledAt = useRef(Number.NEGATIVE_INFINITY)
  const previousTokenExpiryInterval = useRef(DEFAULT_TOKEN_EXPIRY_INTERVAL)
  const lastSignature = useRef(queueSignature(queues))
  const lastTotal = useRef(totalQueued(queues))

  const addLog = (kind, message, detail, extra = {}) => {
    const entry = {
      id: nextLogId.current++,
      time: formatTime(new Date()),
      kind,
      message,
      detail,
      ...extra,
    }
    setLog(entries => [entry, ...entries].slice(0, MAX_LOG_ENTRIES))
  }

  // List SDK events from `AtomicSDK.observeSDKEvents`, with their payloads.
  useEffect(() => {
    if (!showSDKEvents) {
      return undefined
    }

    return subscribeToSDKEvents(event => {
      addLog('event', `SDK event: ${event.eventName}`, undefined, {
        eventName: event.eventName,
        payload: JSON.stringify(event, null, 2),
      })
    })
    // `addLog` only uses refs and state setters.
  }, [showSDKEvents])

  // Record the settings applied before initialise on this page load.
  useEffect(() => {
    const protocol =
      Settings.sdkFeatures.apiProtocol === 'http' ? 'HTTP' : 'WebSockets'
    addLog('info', `Page loaded. API protocol: ${protocol}.`)

    if (Settings.sdkFeatures.eventFlushIntervalOnLoad) {
      const {description} = parseFlushIntervalInput(
        Settings.sdkFeatures.eventFlushIntervalOnLoad
      )
      addLog(
        'info',
        `setEventFlushInterval(${description}) was called before initialise.`
      )
    }
    // Run once on mount only.
  }, [])

  // Poll the stored queues, local storage changes in the same tab do not raise a `storage` event.
  useEffect(() => {
    const refresh = () => {
      const current = readQueues()
      const signature = queueSignature(current)
      if (signature === lastSignature.current) {
        return
      }

      const total = totalQueued(current)
      const previousTotal = lastTotal.current
      lastSignature.current = signature
      lastTotal.current = total
      setQueues(current)

      // A manual flush that settled since the previous poll accounts for the change.
      const manualFlushAccountsForChange =
        flushesInProgressRef.current > 0 ||
        performance.now() - lastFlushSettledAt.current <
          QUEUE_POLL_INTERVAL_MS * 2

      if (total < previousTotal && !manualFlushAccountsForChange) {
        addLog(
          'info',
          `Queue went from ${previousTotal} to ${total} event(s) without a manual flush.`
        )
      }
    }

    const timer = window.setInterval(refresh, QUEUE_POLL_INTERVAL_MS)
    window.addEventListener('storage', refresh)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('storage', refresh)
    }
    // `addLog` only uses refs and state setters, so the poll is set up once.
  }, [])

  // Restore normal token behaviour if the panel is removed while token fetches are failing.
  useEffect(() => {
    return () => {
      setTokenFetchFailure(false)
    }
  }, [])

  const updateFlushesInProgress = change => {
    flushesInProgressRef.current += change
    if (change < 0) {
      lastFlushSettledAt.current = performance.now()
    }
    setFlushesInProgress(flushesInProgressRef.current)
  }

  const runFlush = async label => {
    const started = performance.now()
    updateFlushesInProgress(1)

    try {
      await window.AtomicSDK.flushEvents()
      addLog('success', `${label} resolved after ${elapsedSince(started)} ms.`)
    } catch (error) {
      addLog(
        'error',
        `${label} rejected after ${elapsedSince(started)} ms.`,
        describeError(error)
      )
    } finally {
      updateFlushesInProgress(-1)
    }
  }

  const flush = () => {
    addLog(
      'info',
      `flushEvents() called with ${totalQueued(queues)} event(s) queued.`
    )
    runFlush('flushEvents()')
  }

  const flushConcurrently = () => {
    addLog(
      'info',
      `${CONCURRENT_FLUSH_COUNT} concurrent flushEvents() calls started with ${totalQueued(queues)} event(s) queued.`
    )
    for (let index = 1; index <= CONCURRENT_FLUSH_COUNT; index++) {
      runFlush(`Concurrent call ${index} of ${CONCURRENT_FLUSH_COUNT}`)
    }
  }

  const applyFlushInterval = text => {
    const {value, description} = parseFlushIntervalInput(text)
    let thrown = null

    const warnings = captureSDKWarnings(() => {
      try {
        window.AtomicSDK.setEventFlushInterval(value)
      } catch (error) {
        thrown = error
      }
    })

    if (thrown) {
      addLog(
        'error',
        `setEventFlushInterval(${description}) threw.`,
        describeError(thrown)
      )
      return
    }

    if (warnings.length > 0) {
      addLog(
        'warning',
        `setEventFlushInterval(${description}) called, the SDK replaced the value.`,
        warnings.join('\n')
      )
      return
    }

    addLog('success', `setEventFlushInterval(${description}) called.`)
  }

  const saveIntervalForPageLoad = () => {
    Settings.sdkFeatures.eventFlushIntervalOnLoad = intervalInput
    Settings.save()
    window.location.reload()
  }

  const clearIntervalForPageLoad = () => {
    Settings.sdkFeatures.eventFlushIntervalOnLoad = ''
    Settings.save()
    window.location.reload()
  }

  const changeProtocol = protocol => {
    Settings.sdkFeatures.apiProtocol = protocol
    Settings.save()
    window.location.reload()
  }

  const toggleTokenFetchFailure = enabled => {
    setTokenFetchFails(enabled)
    setTokenFetchFailure(enabled)

    if (enabled) {
      previousTokenExpiryInterval.current =
        window.AtomicSDK.tokenExpiryInterval || DEFAULT_TOKEN_EXPIRY_INTERVAL
      window.AtomicSDK.setTokenExpiryInterval(
        FORCE_TOKEN_REQUEST_EXPIRY_INTERVAL
      )
      addLog(
        'warning',
        'Token fetch failure on. The SDK will receive no token until this is turned off.'
      )
      return
    }

    window.AtomicSDK.setTokenExpiryInterval(previousTokenExpiryInterval.current)
    addLog('info', 'Token fetch failure off. Tokens are fetched normally.')
  }

  const sendCustomEvent = async () => {
    const name = customEventName.trim()
    try {
      await window.AtomicSDK.sendCustomEvent({eventName: name})
      addLog('success', `sendCustomEvent("${name}") resolved.`)
    } catch (error) {
      addLog(
        'error',
        `sendCustomEvent("${name}") rejected.`,
        describeError(error)
      )
    }
  }

  const toggleCollapsed = () => {
    setCollapsed(previous => {
      storeFlag(COLLAPSED_STORAGE_KEY, !previous)
      return !previous
    })
  }

  const toggleShowSDKEvents = show => {
    storeFlag(SHOW_SDK_EVENTS_STORAGE_KEY, show)
    setShowSDKEvents(show)
  }

  const normalisedFilter = sdkEventFilter.trim().toLowerCase()
  const visibleLog = log.filter(
    entry =>
      !entry.eventName ||
      entry.eventName.toLowerCase().includes(normalisedFilter)
  )

  const queuedCount = totalQueued(queues)
  const savedIntervalOnLoad = Settings.sdkFeatures.eventFlushIntervalOnLoad

  if (collapsed) {
    return (
      <button
        type="button"
        className="analytics-panel-toggle button-dark button-small"
        aria-expanded="false"
        aria-controls="analytics-panel"
        onClick={toggleCollapsed}
      >
        Events <span className="analytics-panel-count">{queuedCount}</span>
        <span className="visually-hidden">
          {' '}
          queued, show analytics events panel
        </span>
      </button>
    )
  }

  return (
    <section
      id="analytics-panel"
      className="analytics-panel"
      aria-labelledby="analytics-panel-title"
    >
      <header className="analytics-panel-header">
        <h2 id="analytics-panel-title">Analytics events</h2>
        <button
          type="button"
          className="button-light button-bordered button-small"
          aria-expanded="true"
          aria-controls="analytics-panel"
          onClick={toggleCollapsed}
        >
          Hide
        </button>
      </header>

      <div className="analytics-panel-section">
        <h3>Queued events ({queuedCount})</h3>
        <ul className="analytics-panel-queues">
          {queues.map(queue => (
            <li key={queue.key}>
              <details>
                <summary>
                  {queue.label}:{' '}
                  {queue.malformed ? 'malformed' : queue.events.length}
                </summary>
                {queue.events.length > 0 ? (
                  <ol>
                    {queue.events.map(event => (
                      <li key={event.id}>{eventName(event)}</li>
                    ))}
                  </ol>
                ) : (
                  <p>No events queued.</p>
                )}
              </details>
            </li>
          ))}
        </ul>
      </div>

      <div className="analytics-panel-section">
        <h3>Flush</h3>
        <div className="analytics-panel-row">
          <button
            type="button"
            className="button-dark button-small"
            onClick={flush}
          >
            Flush events
          </button>
          <button
            type="button"
            className="button-light button-bordered button-small"
            onClick={flushConcurrently}
          >
            Flush ×{CONCURRENT_FLUSH_COUNT} concurrently
          </button>
        </div>
        <p className="analytics-panel-hint" aria-live="polite">
          {flushesInProgress > 0
            ? `${flushesInProgress} flush call(s) in progress…`
            : 'No flush in progress.'}
        </p>
      </div>

      <div className="analytics-panel-section">
        <div className="analytics-panel-log-header">
          <h3>Activity log</h3>
          <button
            type="button"
            className="button-light button-small"
            onClick={() => setLog([])}
            disabled={log.length === 0}
          >
            Clear
          </button>
        </div>
        <label className="analytics-panel-checkbox">
          <input
            type="checkbox"
            checked={showSDKEvents}
            onChange={event => toggleShowSDKEvents(event.target.checked)}
          />
          Show SDK events (observeSDKEvents)
        </label>
        {showSDKEvents && (
          <>
            <label
              className="analytics-panel-field analytics-panel-filter-label"
              htmlFor="analytics-event-filter"
            >
              Only show SDK events whose name contains
            </label>
            <input
              id="analytics-event-filter"
              type="text"
              value={sdkEventFilter}
              onChange={event => setSDKEventFilter(event.target.value)}
              placeholder="e.g. card-displayed"
              spellCheck="false"
              autoComplete="off"
            />
          </>
        )}
        <ol className="analytics-panel-log" aria-live="polite">
          {visibleLog.map(entry => (
            <li key={entry.id} className={`analytics-log-${entry.kind}`}>
              <span className="analytics-log-time">{entry.time}</span>{' '}
              {entry.message}
              {entry.detail && <pre>{entry.detail}</pre>}
              {entry.payload && (
                <details>
                  <summary>Payload</summary>
                  <pre>{entry.payload}</pre>
                </details>
              )}
            </li>
          ))}
        </ol>
      </div>
      <div className="analytics-panel-section">
        <h3>Flush interval</h3>
        <label className="analytics-panel-field" htmlFor="analytics-interval">
          Value (seconds; quote for a string, or NaN, Infinity, null, undefined)
        </label>
        <div className="analytics-panel-row">
          <input
            id="analytics-interval"
            type="text"
            value={intervalInput}
            onChange={event => setIntervalInput(event.target.value)}
            spellCheck="false"
            autoComplete="off"
          />
          <button
            type="button"
            className="button-dark button-small"
            onClick={() => applyFlushInterval(intervalInput)}
          >
            Set interval
          </button>
        </div>
        <div className="analytics-panel-row analytics-panel-presets">
          {INTERVAL_PRESETS.map(preset => (
            <button
              key={preset}
              type="button"
              className="button-light button-bordered button-small"
              onClick={() => {
                setIntervalInput(preset)
                applyFlushInterval(preset)
              }}
            >
              {preset}
            </button>
          ))}
        </div>
        <p className="analytics-panel-hint">
          Before initialise on page load:{' '}
          <strong>
            {savedIntervalOnLoad ? savedIntervalOnLoad : 'not set'}
          </strong>
        </p>
        <div className="analytics-panel-row">
          <button
            type="button"
            className="button-light button-bordered button-small"
            onClick={saveIntervalForPageLoad}
          >
            Use value on page load &amp; reload
          </button>
          {savedIntervalOnLoad && (
            <button
              type="button"
              className="button-light button-bordered button-small"
              onClick={clearIntervalForPageLoad}
            >
              Clear &amp; reload
            </button>
          )}
        </div>
      </div>

      <div className="analytics-panel-section">
        <h3>Connection</h3>
        <label className="analytics-panel-field" htmlFor="analytics-protocol">
          API protocol (changing it reloads the page)
        </label>
        <select
          id="analytics-protocol"
          value={
            Settings.sdkFeatures.apiProtocol === 'http' ? 'http' : 'webSockets'
          }
          onChange={event => changeProtocol(event.target.value)}
        >
          <option value="webSockets">WebSockets</option>
          <option value="http">HTTP</option>
        </select>
        <label className="analytics-panel-checkbox">
          <input
            type="checkbox"
            checked={tokenFetchFails}
            onChange={event => toggleTokenFetchFailure(event.target.checked)}
          />
          Fail token fetch
        </label>
      </div>

      <div className="analytics-panel-section">
        <h3>Custom event</h3>
        <label className="analytics-panel-field" htmlFor="analytics-event-name">
          Event name
        </label>
        <div className="analytics-panel-row">
          <input
            id="analytics-event-name"
            type="text"
            value={customEventName}
            onChange={event => setCustomEventName(event.target.value)}
            spellCheck="false"
            autoComplete="off"
          />
          <button
            type="button"
            className="button-dark button-small"
            onClick={sendCustomEvent}
          >
            Send
          </button>
        </div>
      </div>
    </section>
  )
}
